import React, { useEffect, useState } from "react";
import { get, post, del } from "../../utils/fetchAPI";
import { swalDel } from "../../utils/SwalUti";
import { toast } from "react-toastify";
import PageTitle from "../../components/page-title/pageTitle";
import { RiShieldUserLine } from "react-icons/ri";
import { TiUserAddOutline } from "react-icons/ti";
import Loader from "../../components/loader/loder";
import DataTable from "react-data-table-component";
import CustomPagination from "../../components/pagination/CustomPagination";
import { FaEdit, FaTrash, FaFileInvoice, FaHistory } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import InvoicePreview from "../../components/invoice/InvoicePreview";

// Extract the outstanding "due" amount from an order row as a plain string.
// serializeOrder returns Decimal128 amounts as strings; guard against the raw
// { $numberDecimal } object shape too, just in case.
const getDueValue = (row) => {
    const due = row?.amounts?.due;
    if (due == null) return "";
    if (typeof due === "object" && due.$numberDecimal != null) return String(due.$numberDecimal);
    return String(due);
};

// Coerce a serialized amount (string, number, or { $numberDecimal }) to a number.
const toAmount = (v) => {
    if (v == null) return 0;
    if (typeof v === "object" && v.$numberDecimal != null) return Number(v.$numberDecimal) || 0;
    return Number(v) || 0;
};

// Total discount actually applied to an order = sum of per-item discounts + any order-level discount.
// (amounts.discount alone is order-level and is currently always 0; the real discount is per line item.)
const getRowDiscount = (row) => {
    const itemDiscount = (row?.orderItems || []).reduce((sum, it) => sum + toAmount(it?.discount), 0);
    return itemDiscount + toAmount(row?.amounts?.discount);
};

const formatDateTime = (d) =>
    d
        ? new Date(d).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
        : "—";

// Colored status chip for a payment attempt (paid / unpaid / partial_paid / failed / cancelled / refunded).
const paymentStatusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    const cls =
        s === "paid" ? "bg-emerald-50 text-emerald-700"
            : s === "partial_paid" ? "bg-amber-50 text-amber-700"
                : s === "unpaid" ? "bg-gray-100 text-gray-600"
                    : s === "refunded" ? "bg-purple-50 text-purple-700"
                        : "bg-red-50 text-red-600"; // failed / cancelled
    return (
        <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap ${cls}`}>
            {s ? s.replace(/_/g, " ") : "—"}
        </span>
    );
};

// Order statuses shown in the status filter (mirrors orderEnums.orderStatus on the API).
const STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "awaiting_payment", label: "Awaiting Payment" },
    { value: "paid", label: "Paid" },
    { value: "partially_paid", label: "Partially Paid" },
    { value: "cancelled", label: "Cancelled" },
    { value: "refunded", label: "Refunded" },
];

const OrderList = () => {
    const [order, setOrder] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [paymentData, setPaymentData] = useState({
        amount: "",
        method: "cash",
        channel: "admin",
        gatewayRef: "",
    });
    const [paymentDue, setPaymentDue] = useState("");
    const [isPaying, setIsPaying] = useState(false);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState(null);

    // Payments-history modal (all payments recorded against one invoice)
    const [showPaymentsModal, setShowPaymentsModal] = useState(false);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsInfo, setPaymentsInfo] = useState(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState("");
    const [doctorFilter, setDoctorFilter] = useState("");
    const [showDeleted, setShowDeleted] = useState(false);
    const [doctors, setDoctors] = useState([]);

    useEffect(() => {
        fetchOrderList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, statusFilter, doctorFilter, showDeleted]);

    // Doctor options for the doctor-wise filter (same source the create form uses).
    useEffect(() => {
        get("/all-user/list")
            .then((res) => setDoctors(res?.data?.users || []))
            .catch(() => setDoctors([]));
    }, []);

    const fetchOrderList = async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ page: currentPage, limit: perPage });
            if (statusFilter) params.append("status", statusFilter);
            if (doctorFilter) params.append("doctor", doctorFilter);
            if (showDeleted) params.append("onlyDeleted", "true");
            const response = await get(`/orders?${params.toString()}`);
            setOrder(response?.data?.data || []);
        } catch (error) {
            toast.error(error?.message || "Failed to fetch orders");
        } finally {
            setIsLoading(false);
        }
    };

    // Reset to the first page whenever a filter changes so results aren't hidden on a stale page.
    const onFilterChange = (setter) => (value) => {
        setCurrentPage(1);
        setter(value);
    };

    const handleEdit = (orderId) => {
        navigate(`/order/${orderId}`, { state: { orderId } });
    };

    const handleDelete = async (orderId) => {
        const result = await swalDel("Delete this order? It will be hidden from the list but can be restored.");
        if (!result?.isConfirmed) return;
        try {
            await del(`/order/${orderId}`);
            toast.success("Order deleted successfully");
            fetchOrderList();
        } catch (error) {
            toast.error(
                error?.response?.data?.error ||
                error?.message ||
                "Failed to delete order"
            );
        }
    };

    const handleViewInvoice = async (orderId) => {
        try {
            // Fetch full order details for invoice
            const response = await get(`/order/${orderId}`);
            setSelectedOrderForInvoice(response?.data);
            setShowInvoiceModal(true);
        } catch (error) {
            toast.error('Failed to load order details for invoice');
        }
    };

    const handleViewPayments = async (orderId) => {
        setShowPaymentsModal(true);
        setPaymentsLoading(true);
        setPaymentsInfo(null);
        try {
            const response = await get(`/order/${orderId}/payments`);
            setPaymentsInfo(response?.data || null);
        } catch (error) {
            toast.error(
                error?.response?.data?.error ||
                error?.message ||
                "Failed to load payments"
            );
            setShowPaymentsModal(false);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const handleClosePaymentsModal = () => {
        setShowPaymentsModal(false);
        setPaymentsInfo(null);
    };

    const confirmStatus = async (id) => {
        try {
            const payload = { status: "confirmed" };
            const response = await post(`/order/${id}/submit`, payload);

            console.log("Server response:", response.data);
            toast.success("Order status updated successfully!");
            await fetchOrderList();
        } catch (error) {
            console.error("Error updating order status:", error);
            toast.error(
                error?.response?.data?.message ||
                "An unexpected error occurred while updating order status."
            );
        }
    };

    const handleOpenModal = (row) => {
        const due = getDueValue(row);
        setSelectedOrderId(row._id);
        setPaymentDue(due);
        setPaymentData({
            amount: due, // default to the outstanding due; the user can edit it below
            method: "cash",
            channel: "admin",
            gatewayRef: "",
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setPaymentDue("");
        setPaymentData({
            amount: "",
            method: "cash",
            channel: "admin",
            gatewayRef: "",
        });
    };

    const confirmPayment = async () => {
        if (isPaying) return; // guard against double-submit → double payment
        setIsPaying(true);
        try {
            await post(`/order/${selectedOrderId}/mark-paid`, paymentData);
            toast.success("Payment marked as paid!");
            handleCloseModal();
            fetchOrderList();
        } catch (error) {
            console.error("Payment confirmation error:", error);
            toast.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "An error occurred during payment confirmation."
            );
        } finally {
            setIsPaying(false);
        }
    };

    const columns = [
        {
            name: "Billing To",
            selector: (row) => (
                <div className="flex flex-col py-2">
                    <span className="font-medium">{row?.billTo?.name}</span>
                    <span className="text-sm text-gray-600">{row?.billTo?.email}</span>
                    <span className="text-sm text-gray-600">{row?.billTo?.phone}</span>
                </div>
            ),
            width: "260px",
        },
        {
            name: "Billing Date",
            selector: (row) =>
                row?.createdAt
                    ? new Date(row.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    })
                    : "N/A",
            sortable: true,
        },
        {
            name: "Status",
            cell: (row) => (
                <div className="flex flex-col gap-1 py-1">
                    <span className="capitalize">{row?.status || "N/A"}</span>
                    {row?.deleted && (
                        <span className="text-[10px] font-semibold text-red-600 bg-red-50 rounded px-1.5 py-0.5 w-fit">
                            Deleted
                        </span>
                    )}
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => String(a?.status || "").localeCompare(String(b?.status || "")),
            wrap: true,
        },
        {
            name: "Payment Status",
            selector: (row) => row?.paymentStatus || "N/A",
            sortable: true,
            wrap: true,
        },
        {
            name: "Amount (৳)",
            cell: (row) => {
                const total = toAmount(row?.amounts?.total);
                const discount = getRowDiscount(row);
                const paid = toAmount(row?.amounts?.paid);
                const due = toAmount(row?.amounts?.due);

                const lines = [
                    { label: "Total", value: total, cls: "text-gray-800 font-semibold" },
                    { label: "Discount", value: discount, cls: "text-amber-600" },
                    { label: "Paid", value: paid, cls: "text-emerald-600" },
                    { label: "Due", value: due, cls: due > 0 ? "text-red-500" : "text-gray-500" },
                ];

                return (
                    <div className="flex flex-col gap-0.5 py-2 text-xs w-full">
                        {lines.map((l) => (
                            <div key={l.label} className="flex justify-between gap-3">
                                <span className="text-gray-500">{l.label}</span>
                                <span className={`font-medium ${l.cls}`}>৳{l.value.toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                );
            },
            sortable: true,
            sortFunction: (a, b) => toAmount(a?.amounts?.total) - toAmount(b?.amounts?.total),
            width: "190px",
            wrap: true,
        },
        {
            name: "Actions",
            selector: (row) => {
                const isPaid = row?.paymentStatus?.toLowerCase() === "paid";
                const isConfirmed = ["awaiting_payment", "paid"].includes(row?.status?.toLowerCase());

                // Deleted orders are read-only — the only allowed action is previewing /
                // downloading the invoice.
                if (row?.deleted) {
                    return (
                        <div className="flex gap-2 w-fit flex-wrap">
                            <button
                                className="p-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                                onClick={() => handleViewInvoice(row._id)}
                                title="View Invoice"
                            >
                                <FaFileInvoice />
                            </button>

                            <button
                                className="p-1 bg-teal-600 text-white rounded hover:bg-teal-700"
                                onClick={() => handleViewPayments(row._id)}
                                title="Payments"
                            >
                                <FaHistory />
                            </button>
                        </div>
                    );
                }

                return (
                    <div className="flex gap-2 w-fit flex-wrap">
                        <button
                            className={`p-1 text-white rounded ${isPaid ? "bg-gray-400": "bg-blue-500 "}`}
                            onClick={() => handleEdit(row._id)}
                            disabled={isPaid}
                            title="Edit"
                        >
                            <FaEdit />
                        </button>

                        <button
                            className="p-1 bg-red-500 text-white rounded"
                            onClick={() => handleDelete(row._id)}
                            title="Delete"
                        >
                            <FaTrash />
                        </button>

                        {/* Invoice button */}
                        <button
                            className="p-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                            onClick={() => handleViewInvoice(row._id)}
                            title="View Invoice"
                        >
                            <FaFileInvoice />
                        </button>

                        {/* Payments history button */}
                        <button
                            className="p-1 bg-teal-600 text-white rounded hover:bg-teal-700"
                            onClick={() => handleViewPayments(row._id)}
                            title="Payments"
                        >
                            <FaHistory />
                        </button>

                        {/* Confirm button */}
                        <button
                            className={`px-3 py-1 rounded text-white ${
                                isConfirmed
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-green-500 hover:bg-green-600"
                            }`}
                            onClick={() => {
                                if (!isConfirmed) {
                                    setSelectedId(row._id);
                                    setShowConfirmModal(true);
                                }
                            }}
                            disabled={isConfirmed}
                        >
                            {isConfirmed ? "Confirmed" : "Confirm"}
                        </button>


                        {/* Pay button */}
                        <button
                            className={`px-3 py-1 rounded text-white ${isPaid ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"}`}
                            onClick={() => !isPaid && handleOpenModal(row)}
                            disabled={isPaid}
                        >
                            {isPaid ? "Paid" : "Pay"}
                        </button>
                    </div>
                );
            },
            wrap: true,
            width: '350px'
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: "#50b6b5",
                color: "white",
                fontSize: "1rem",
                fontWeight: "bold",
            },
        },
        rows: {
            style: { fontSize: "0.9rem" },
        },
    };

    return (
        <div className="flex flex-col gap-5 w-full">
            <div className="flex justify-between items-center">
                <PageTitle title="Orders List" icon={<RiShieldUserLine />} />
                <button
                    onClick={() => navigate("/order-create")}
                    className="flex items-center gap-3 text-sm px-4 py-2 bg-primary text-white rounded-md"
                >
                    <TiUserAddOutline className="w-5 h-5" />
                    Create New Order
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-4 bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => onFilterChange(setStatusFilter)(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[180px] focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                        <option value="">All statuses</option>
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-500 mb-1">Doctor</label>
                    <select
                        value={doctorFilter}
                        onChange={(e) => onFilterChange(setDoctorFilter)(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[200px] focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                        <option value="">All doctors</option>
                        {doctors.map((d) => (
                            <option key={d._id} value={d._id}>{d.name}</option>
                        ))}
                    </select>
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700 py-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={showDeleted}
                        onChange={(e) => onFilterChange(setShowDeleted)(e.target.checked)}
                        className="w-4 h-4 accent-primary"
                    />
                    Show deleted
                </label>

                {(statusFilter || doctorFilter || showDeleted) && (
                    <button
                        type="button"
                        onClick={() => {
                            setCurrentPage(1);
                            setStatusFilter("");
                            setDoctorFilter("");
                            setShowDeleted(false);
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700 underline py-2"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {isLoading ? (
                <div className="text-center text-gray-500">
                    <Loader />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={order}
                    customStyles={customStyles}
                    highlightOnHover
                    striped
                />
            )}

            <CustomPagination
                currentPage={currentPage}
                perPage={perPage}
                setCurrentPage={setCurrentPage}
                setPerPage={setPerPage}
            />

            {/* 🔹 Payment Modal */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-[400px] relative">
                        <h2 className="text-lg font-semibold text-center mb-4">
                            Confirm Payment
                        </h2>

                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700">
                                Amount
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={paymentData.amount}
                                onChange={(e) =>
                                    setPaymentData({ ...paymentData, amount: e.target.value })
                                }
                                className="w-full border rounded px-3 py-2 mt-1"
                            />
                            {paymentDue !== "" && (
                                <div className="flex items-center justify-between text-xs mt-1">
                                    <span className="text-gray-500">
                                        Amount due:{" "}
                                        <span className="font-semibold text-gray-700">৳{paymentDue}</span>
                                    </span>
                                    {String(paymentData.amount) !== String(paymentDue) && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setPaymentData((p) => ({ ...p, amount: paymentDue }))
                                            }
                                            className="text-teal-600 hover:underline font-medium"
                                        >
                                            Pay full due
                                        </button>
                                    )}
                                </div>
                            )}

                            <label className="block text-sm font-medium text-gray-700">
                                Method
                            </label>
                            <select
                                value={paymentData.method}
                                onChange={(e) =>
                                    setPaymentData({ ...paymentData, method: e.target.value })
                                }
                                className="w-full border rounded px-3 py-2 mt-1"
                            >
                                <option value="cash">Cash</option>
                                <option value="card">Card</option>
                                <option value="bkash">bKash</option>
                                <option value="nagad">Nagad</option>
                            </select>

                            {/* Channel is hidden — always sent as the default "admin" (see paymentData state) */}

                            <label className="block text-sm font-medium text-gray-700">
                                Gateway Ref
                            </label>
                            <input
                                type="text"
                                value={paymentData.gatewayRef}
                                onChange={(e) =>
                                    setPaymentData({ ...paymentData, gatewayRef: e.target.value })
                                }
                                className="w-full border rounded px-3 py-2 mt-1"
                            />
                        </div>

                        <div className="flex justify-end mt-6 space-x-3">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmPayment}
                                disabled={isPaying}
                                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isPaying ? "Processing..." : "Confirm Payment"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔹 Payments History Modal */}
            {showPaymentsModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">Payments</h2>
                                {paymentsInfo && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Invoice #{paymentsInfo.customInvoiceNumber || paymentsInfo.orderNumber}
                                        {paymentsInfo.paymentStatus && (
                                            <span className="ml-2 capitalize">
                                                · {String(paymentsInfo.paymentStatus).replace(/_/g, " ")}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={handleClosePaymentsModal}
                                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                                title="Close"
                            >
                                &times;
                            </button>
                        </div>

                        <div className="px-6 py-4 overflow-y-auto">
                            {paymentsLoading ? (
                                <div className="py-8 flex justify-center">
                                    <Loader />
                                </div>
                            ) : paymentsInfo ? (
                                <>
                                    {/* Totals strip */}
                                    <div className="grid grid-cols-3 gap-3 mb-4">
                                        {[
                                            { label: "Total", value: toAmount(paymentsInfo.amounts?.total), cls: "text-gray-800" },
                                            { label: "Paid", value: toAmount(paymentsInfo.amounts?.paid), cls: "text-emerald-600" },
                                            {
                                                label: "Due",
                                                value: toAmount(paymentsInfo.amounts?.due),
                                                cls: toAmount(paymentsInfo.amounts?.due) > 0 ? "text-red-500" : "text-gray-500",
                                            },
                                        ].map((t) => (
                                            <div key={t.label} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                                <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                                    {t.label}
                                                </div>
                                                <div className={`text-base font-bold ${t.cls}`}>৳{t.value.toFixed(2)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {paymentsInfo.payments?.length ? (
                                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                                                        <th className="text-left font-semibold px-3 py-2 w-10">#</th>
                                                        <th className="text-left font-semibold px-3 py-2">Date</th>
                                                        <th className="text-right font-semibold px-3 py-2">Amount</th>
                                                        <th className="text-left font-semibold px-3 py-2">Method</th>
                                                        <th className="text-left font-semibold px-3 py-2">Channel</th>
                                                        <th className="text-left font-semibold px-3 py-2">Ref</th>
                                                        <th className="text-left font-semibold px-3 py-2">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {paymentsInfo.payments.map((p) => (
                                                        <tr key={p._id} className="hover:bg-gray-50/70">
                                                            <td className="px-3 py-2 text-gray-500">{p.attemptNo}</td>
                                                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                                                {formatDateTime(p.receivedAt || p.createdAt)}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-semibold text-gray-900 whitespace-nowrap">
                                                                ৳{toAmount(p.amount).toFixed(2)}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-700 capitalize">
                                                                {String(p.method || "—").replace(/_/g, " ")}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-700 capitalize">
                                                                {String(p.channel || "—").replace(/_/g, " ")}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-500 break-all">{p.gatewayRef || "—"}</td>
                                                            <td className="px-3 py-2">{paymentStatusBadge(p.status)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                                            No payments have been recorded for this invoice yet.
                                        </div>
                                    )}
                                </>
                            ) : null}
                        </div>

                        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
                            <button
                                onClick={handleClosePaymentsModal}
                                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md p-6 w-96 shadow-lg">
                        <h3 className="text-lg font-semibold mb-2">
                            Confirm Action
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to confirm this?
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    setSelectedId(null);
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="px-4 py-2 rounded bg-green-500 text-white hover:bg-green-600"
                                onClick={() => {
                                    confirmStatus(selectedId);
                                    setShowConfirmModal(false);
                                    setSelectedId(null);
                                }}
                            >
                                Yes, Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Invoice Preview Modal */}
            {showInvoiceModal && selectedOrderForInvoice && (
                <InvoicePreview
                    order={selectedOrderForInvoice}
                    onClose={() => {
                        setShowInvoiceModal(false);
                        setSelectedOrderForInvoice(null);
                    }}
                />
            )}

        </div>
    );
};

export default OrderList;
